import { Action } from '@wix/add-panel-common';
import { useCallback, useContext, useMemo } from 'react';
import { ActionsDispatcherContext } from './ActionsDispatcherContext';
import type { UseActionsDispatcher, UseGenericActionsDispatcher } from './ActionsDispatcher.types';

export const useGenericActionsDispatcher = (): UseGenericActionsDispatcher => {
  const { dispatchAction } = useContext(ActionsDispatcherContext);

  return useCallback(
    (type, payload) => {
      dispatchAction({ type, payload });
    },
    [dispatchAction],
  );
};

export const useActionsDispatcher = (): UseActionsDispatcher => {
  const { dispatchAction } = useContext(ActionsDispatcherContext);

  return useMemo(
    () => ({
      dispatchCategoryClick: (payload) => dispatchAction({ type: Action.CategoryClick, payload }),
      dispatchCloseAddPanel: () => dispatchAction({ type: Action.CloseAddPanel }),
      dispatchFedopsInteractionStarted: (payload) =>
        dispatchAction({
          type: Action.FedopsInteractionStarted,
          payload,
        }),
      dispatchFedopsInteractionEnded: (payload) => dispatchAction({ type: Action.FedopsInteractionEnded, payload }),
      dispatchHelpClick: (payload) => dispatchAction({ type: Action.HelpClick, payload }),
      dispatchCategoryOpen: (payload) => dispatchAction({ type: Action.CategoryOpen, payload }),
      dispatchSectionOpen: (payload) => dispatchAction({ type: Action.SectionOpen, payload }),
      dispatchSectionClick: (payload) => dispatchAction({ type: Action.SectionClick, payload }),
      dispatchUnmount: (payload) => dispatchAction({ type: Action.Unmount, payload }),
      dispatchPlayVideo: (payload) => dispatchAction({ type: Action.PlayVideo, payload }),
      dispatchComponentClick: (payload) => dispatchAction({ type: Action.ComponentClick, payload }),
      dispatchComponentMouseEnter: (payload) => dispatchAction({ type: Action.ComponentMouseEnter, payload }),
      dispatchComponentMouseLeave: (payload) => dispatchAction({ type: Action.ComponentMouseLeave, payload }),
      dispatchMouseUp: (payload) => dispatchAction({ type: Action.MouseUp, payload }),
      dispatchPathUpdate: (payload) => dispatchAction({ type: Action.PathUpdate, payload }),
      dispatchStartItemDrag: (payload) => dispatchAction({ type: Action.StartItemDrag, payload }),
      dispatchOpenMediaManager: (payload) => dispatchAction({ type: Action.OpenMediaManager, payload }),
      dispatchAddBlankPage: (payload) => dispatchAction({ type: Action.AddBlankPage, payload }),
      dispatchAddQuickActionBar: (payload) => dispatchAction({ type: Action.AddQuickActionBar, payload }),
      dispatchAddBackToTopButton: (payload) => dispatchAction({ type: Action.AddBackToTopButton, payload }),
      dispatchAddWelcomeScreen: (payload) => dispatchAction({ type: Action.AddWelcomeScreen, payload }),
      dispatchOpenPhotoStudio: (payload) => dispatchAction({ type: Action.OpenPhotoStudio, payload }),
      dispatchAddChromeThemeColor: (payload) => dispatchAction({ type: Action.AddChromeThemeColor, payload }),
      dispatchAddPreset: (payload) => dispatchAction({ type: Action.AddPreset, payload }),
      dispatchAddCollection: () => dispatchAction({ type: Action.AddCollection }),
      dispatchAddDataSet: (payload) => dispatchAction({ type: Action.AddDataSet, payload }),
      dispatchCreateNewForm: () => dispatchAction({ type: Action.CreateNewForm }),
      dispatchAddExistingForm: () => dispatchAction({ type: Action.AddExistingForm }),
      dispatchAddFormDataSet: (payload) => dispatchAction({ type: Action.AddFormDataSet, payload }),
      dispatchInstallAppAndNotify: (payload) => dispatchAction({ type: Action.InstallAppAndNotify, payload }),
      dispatchAddRelatedApp: (payload) => dispatchAction({ type: Action.AddRelatedApp, payload }),
      dispatchDragRelatedApp: (payload) => dispatchAction({ type: Action.DragRelatedApp, payload }),
      dispatchOpenWixAppMarket: (payload) => dispatchAction({ type: Action.OpenWixAppMarket, payload }),
      dispatchInstallSuperApp: (payload) => dispatchAction({ type: Action.InstallSuperApp, payload }),
      dispatchSearchButtonClick: () => dispatchAction({ type: Action.SearchButtonClick }),
      dispatchToggleDevMode: (payload) => dispatchAction({ type: Action.ToggleDevMode, payload }),
      dispatchOpenAccountSettings: (payload) => dispatchAction({ type: Action.OpenAccountSettings, payload }),
      dispatchReloadData: () => dispatchAction({ type: Action.ReloadData }),
      dispatchOpenLeftbarSidePanelAction: (payload) => dispatchAction({ type: Action.OpenLeftbarSidePanel, payload }),
      dispatchStartMyWidgetDrag: (payload) => dispatchAction({ type: Action.MyWidgetsStartDrag, payload }),
    }),
    [dispatchAction],
  );
};
